import React from 'react';
import _every from 'lodash/every';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import * as formsActions from '../../store/forms/actions';
import * as fieldsActions from '../../store/fields/actions';
import * as reducers from '../../store/reducers';

import styles from './Form.scss';
import Button from 'Components/Button';
import FlashMessage from 'Components/FlashMessage';
import Lightbox from 'Components/Lightbox';
import LoginAgainForm from 'Containers/LoginAgainForm';
import ConnectedFormattedMessage from 'Containers/ConnectedFormattedMessage';
import FormFieldGroup from './FormFieldGroup';

class Form extends React.Component {
  constructor(props) {
    super(props);

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.validateForm = this.validateForm.bind(this);
  }

  componentDidMount() {
    const {
      initialFormSettings,
      initialFieldSettings,
      initializeForm,
      type,
      id,
    } = this.props;
    initializeForm(type, id, initialFormSettings, initialFieldSettings);
  }

  componentWillReceiveProps(nextProps) {
    const {
      isSensitiveTokenExpired,
      form,
      id,
      type,
      initializeForm,
      initialFormSettings,
      initialFieldSettings,
    } = nextProps;
    if (
      form &&
      this.props.form &&
      !form.isAwaitingUpdatedToken &&
      !isSensitiveTokenExpired &&
      form.isAwaitingUpdatedToken !== this.props.form.isAwaitingUpdatedToken
    ) {
      this.handleSubmit();
    }

    if (this.props.id !== id)
      initializeForm(type, id, initialFormSettings, initialFieldSettings);

    if (
      this.props.form &&
      form &&
      form.message &&
      this.props.form.message !== form.message &&
      form.message.type === 'success' &&
      this.props.animationType !== 'persist'
    ) {
      window.setTimeout(nextProps.clearFormMessage, 5000, id, type);
    }
  }

  componentWillUnmount() {
    const { removeFields, type, id } = this.props;
    removeFields(type, id);
  }

  handleInputChange(target) {
    const { type, checked, value, name, validity } = target;
    const { fields, id } = this.props;
    const field = fields[name];
    const newValue = type === 'checkbox' ? checked : value;

    // Sometimes, a field must match (or not match!) another field.
    // We use the browser’s custom validity function to store the result.
    let customValidity =
      field.cannotMatch === value ? field.stickyErrorMsgId : '';
    if (field.mustMatch) {
      const fieldToMatch = fields[`${id}__${field.mustMatch}`];
      if (newValue !== fieldToMatch.value) {
        field.stickyErrorMsgId = 'form.field.error_text.must_match';
        field.stickyErrorMsgValues = {
          field_label: <ConnectedFormattedMessage id={fieldToMatch.labelId} />,
        };
        customValidity = field.stickyErrorMsgId;
      }
    }
    target.setCustomValidity(customValidity);

    const changeFunc = this.props.handleFieldChange
      ? this.props.handleFieldChange
      : this.props.updateFieldValue;
    changeFunc({ ...field, name }, newValue, validity);
  }

  validateForm() {
    const newFields = { ...this.props.fields };
    const isValid = _every(newFields, 'isValid');
    return isValid;
  }

  handleSubmit(e) {
    if (e && e.preventDefault) e.preventDefault();
    const isFormValid = this.validateForm();
    const {
      submitHandler,
      fields,
      submitForm,
      submitArgs,
      enableFormReconciliationMode,
      type,
      id,
    } = this.props;

    if (submitHandler) {
      submitHandler(fields, isFormValid);
    } else if (isFormValid) {
      submitForm(type, id, submitArgs);
    } else {
      enableFormReconciliationMode(type, id);
    }
  }

  processFields(fields) {
    const fieldsArray = this.convertFieldsToArray(fields);
    const splitFields = this.splitOutHiddenOnlyFields(fieldsArray, fields);
    return splitFields;
  }

  convertFieldsToArray(fields) {
    const fieldsArray = [];
    Object.keys(fields).map((key) => fieldsArray.push({ key, ...fields[key] }));
    return fieldsArray;
  }

  splitOutHiddenOnlyFields(fieldsArray, fields) {
    const groupedFields = this.groupFields(fields);
    const splitFields = {
      visible: groupedFields,
      hidden: [],
    };
    fieldsArray.map((field) => {
      if (field.hidden) {
        splitFields.hidden.push(field);
      }
    });
    return splitFields;
  }

  groupFields(fields) {
    const { form, id } = this.props;
    const { fieldNames, fieldGroups } = form;
    const groups = fieldGroups || [{ keys: fieldNames }];
    return groups.map((group) => {
      const newGroup = {
        ...group,
        fields: group.keys.map((simpleKey) => {
          const key = `${id}__${simpleKey}`;
          return { key, ...fields[key] };
        }),
      };

      return newGroup;
    });
  }

  render() {
    const {
      form,
      fields,
      formStyle,
      formNote,
      submitButtonType,
      submitButtonLabelValues,
      submitButtonLoadingNote,
      type,
      id,
      messagePosition,
      messageFormat,
      flashDialog,
      inLightbox,
      isReadOnly,
      preSubmitNote,
    } = this.props;
    if (!fields || !form) return null;
    const {
      message,
      isPosting,
      isReconciling,
      readOnly,
      isAwaitingUpdatedToken,
    } = form;
    const isFormReadOnly = isReadOnly || readOnly === true;
    const processedFields = this.processFields(fields);
    const isLoading = isPosting || this.props.isPosting;
    return (
      <form
        className={`Form${formStyle ? ` Form--${formStyle}` : ''}${
          isFormReadOnly ? ' Form--read-only' : ''
        }`}
        noValidate
        onSubmit={(e) => this.handleSubmit(e)}
      >
        {message && messageFormat === 'flash' ? (
          <FlashMessage
            {...message}
            dialog={flashDialog}
            animationType={message.animationType || 'fade'}
            location={messagePosition}
          />
        ) : null}
        {processedFields.visible.map((group, i) => (
          <FormFieldGroup
            {...this.props}
            key={`group-${group.id || i}`}
            group={group}
            handleInputChange={this.handleInputChange}
            isFormReadOnly={isFormReadOnly}
          />
        ))}
        <React.Fragment>
          {processedFields.hidden.map((field) => (
            <input
              key={`field-${field.key}`}
              type="hidden"
              name={field.key}
              value={field.value}
            />
          ))}
        </React.Fragment>
        {preSubmitNote}
        <div className="Form__action">
          <Button
            isSubmit
            buttonType={submitButtonType}
            label={
              <ConnectedFormattedMessage
                id={`form.${type}.submit_button_label`}
                values={submitButtonLabelValues}
              />
            }
            loading={isLoading}
            additionalClasses={isReconciling ? ' has-new-error' : ''}
            disabled={
              (!this.validateForm() && isReconciling) ||
              isLoading ||
              isFormReadOnly
            }
          />
          {submitButtonLoadingNote && isLoading ? (
            <div className="Form__loading-note">{submitButtonLoadingNote}</div>
          ) : null}
        </div>
        {formNote}
        {!inLightbox ? (
          <Lightbox
            type="small"
            visible={isAwaitingUpdatedToken}
            closeHandler={(e) => this.props.resolveFormTokenUpdate(type, id)}
            header={
              <ConnectedFormattedMessage id="form.loginAgain.lightbox_title" />
            }
          >
            <p>Enter your password to save your changes.</p>
            <LoginAgainForm referringFormName={type} referringFormId={id} />
          </Lightbox>
        ) : null}
      </form>
    );
  }
}

const mapStateToProps = (state, { id }) => ({
  form: reducers.getForm(state, id),
  fields: reducers.getFormFields(state, id),
  isSensitiveTokenExpired: reducers.isSensitiveTokenExpired(state),
});

Form.propTypes = {
  /** A unique ID to avoid collisions in redux */
  id: PropTypes.string.isRequired,
  /** The type determines much of the configuration */
  type: PropTypes.string.isRequired,
  /** Provided by Redux, it contains form configurations */
  form: PropTypes.shape({
    /** The IDs of the fields included. */
    fieldNames: PropTypes.array.isRequired,
    /** The field that will be focused when the form is loaded */
    autoFocusField: PropTypes.string,
    /** The field ID for the currently focused field. */
    focusedField: PropTypes.string,
    /** The label text inside the submit button */
    successMessage: PropTypes.string,
    /**
     * When true, it only provides the fields that have changed to the
     * function that calls the appropriate API endpoint.
     */
    onlySubmitChanges: PropTypes.bool,
    /**
     * If fields need to be grouped together, provide an array of groups.
     * Each group must contain a 'keys' property (with an array of
     * field names) and an optional 'title' property, which is a string.
     */
    fieldGroups: PropTypes.array,
    /**
     * This is provides a message object, which has at least a type and text
     */
    message: PropTypes.object,
    /** After submitting, this is true until the submit callback resolves */
    isPosting: PropTypes.bool,
    /**
     * When true, this will show any field errors. It is set to true if the
     * user submits the form and any fields do not meet the requirements.
     */
    isReconciling: PropTypes.bool,
    /** This will set all fields in the form to be read only */
    readOnly: PropTypes.bool,
    /**
     * Some forms require the user to be recently logged-in to make a
     * sensitive change. When true, it shows the user a modal to re-enter
     * their password.
     */
    isAwaitingUpdatedToken: PropTypes.bool,
  }),
  /**
   * These are both overrides for the form settings, which is bad.
   * This needs to be revisited. @TODO
   */
  isPosting: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  /** Indicates when a user token is too old for sensitive actions */
  isSensitiveTokenExpired: PropTypes.bool,
  /** Provided by Redux, it has an associative array of the form’s fields */
  fields: PropTypes.object,
  /** Initializing the form adds (or resets) it in the Redux storm */
  initializeForm: PropTypes.func,
  /** A way to set some attributes to something other than the default. */
  initialFormSettings: PropTypes.object,
  /** A way to set some attributes to something other than the default. */
  initialFieldSettings: PropTypes.object,
  /** This determines position of flash messages relative to the form. */
  messagePosition: PropTypes.string,
  /**
   * Most messages appear at the top of the form, but some one-field forms
   * display them inline.
   */
  messageFormat: PropTypes.oneOf(['flash', 'inline']),
  /** Used to modify any styles specific to a type of form */
  formStyle: PropTypes.oneOf([
    'login',
    'one-line',
    'add-user',
    'add-user-student',
    'add-school-user',
    'change-password',
    'createClass',
    'acceptTeacherInvitation',
    'accountBasics',
    'editUser',
    'full-width',
  ]),
  /**
   * This is (typically) displayed below the form and provides context or
   * ancillary information to help the user.
   */
  formNote: PropTypes.node,
  /** Determines the style of the submit button */
  submitButtonType: PropTypes.oneOf(['primary', 'secondary', 'add-user']),
  /**
   * In the event the submit button translated string has a dynamic value,
   * it will be passed in here.
   */
  submitButtonLabelValues: PropTypes.object,
  /**
   * Sometimes, we want to display a message alongside the button while it
   * is loading. For example, “Please don’t hit the back button.”
   */
  submitButtonLoadingNote: PropTypes.string,
  /**
   * Typically, we use a standard Redux action to deal with form submission,
   * but this allows a form to provide a custom handler.
   */
  submitHandler: PropTypes.func,
  /** Rarely, a form requires a true/false dialog after submission. */
  flashDialog: PropTypes.element,
  /**
   * This is used exclusively to avoid nested lightboxes. It seems like
   * this could be determined another way, but it works for now.
   */
  inLightbox: PropTypes.bool,
};

Form.defaultProps = {
  messagePosition: 'container-header',
  messageFormat: 'flash',
  isReadOnly: false,
};

export default connect(mapStateToProps, {
  ...formsActions,
  ...fieldsActions,
})(Form);
